/**
 * Language. Malayalam and English on every applicant facing string and every
 * generated document. SPEC §7.3.
 *
 * The language is chosen at first use and is changeable at any time. Strings
 * are `Bilingual` objects rather than keys into a bundle, so a string that is
 * missing its Malayalam is a type error and not a silent fallback to English.
 */

import { createContext, createElement, useCallback, useContext, useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import type { Bilingual, Lang } from "@ksidc/ontology";

const STORAGE_KEY = "ksidc.lang";

interface LangContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** The string in the chosen language. */
  t: (text: Bilingual) => string;
  /** Both languages, for generated documents and printed pages. */
  both: (text: Bilingual) => string;
}

const LangContext = createContext<LangContextValue | null>(null);

function stored(): Lang | null {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "ml" || v === "en" ? v : null;
  } catch {
    return null;
  }
}

export function LangProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: Lang;
}): ReactElement {
  const [lang, setLangState] = useState<Lang>(() => stored() ?? initial ?? "en");

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // A browser refusing storage should not stop the language changing.
    }
  }, []);

  const t = useCallback((text: Bilingual) => (lang === "ml" ? text.ml || text.en : text.en), [lang]);
  const both = useCallback((text: Bilingual) => `${text.en}\n${text.ml}`, []);

  return createElement(LangContext.Provider, { value: { lang, setLang, t, both } }, children);
}

export function useLang(): LangContextValue {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error("useLang must be used inside LangProvider");
  return ctx;
}

/** Shorthand for a bilingual literal, so screens read as prose. */
export const bi = (en: string, ml: string): Bilingual => ({ en, ml });
